import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus, Trash2, Save, RefreshCw, Loader2, AlertCircle, Search, ChevronDown,
  X, EyeOff, Link2, Sparkles,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { brandService, type BrandInput, type BrandAliasInput, type UnmappedBrand } from '../../services/brandService';
import type { Brand, BrandAlias, BrandMatchType } from '../../types/domain';

// Native button styles (the app's <Button> is react-aria based; native buttons keep
// onClick/title/disabled simple here). Kept in sync with CataloguesPanel.
const BTN = 'inline-flex items-center justify-center gap-1.5 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none';
const btnPrimary = `${BTN} h-9 px-3 border-transparent bg-primary text-primary-foreground hover:opacity-90`;
const btnPrimarySm = `${BTN} h-8 px-2.5 border-transparent bg-primary text-primary-foreground hover:opacity-90`;
const btnOutline = `${BTN} h-9 px-3 border-border bg-transparent hover:bg-muted`;
const btnOutlineSm = `${BTN} h-8 px-2.5 border-border bg-transparent hover:bg-muted`;
const btnGhostSm = `${BTN} h-8 px-2 border-transparent bg-transparent hover:bg-muted`;
const field = 'h-9 w-full rounded-md border border-input bg-background px-2.5 text-sm focus:border-primary focus:outline-none';
const fieldSm = 'h-8 rounded-md border border-input bg-background px-2 text-sm focus:border-primary focus:outline-none';

// Deterministic accent per brand so logo-less rows still read as distinct.
const ACCENTS = [
  'bg-teal-500/15 text-teal-400', 'bg-sky-500/15 text-sky-400', 'bg-violet-500/15 text-violet-400',
  'bg-amber-500/15 text-amber-400', 'bg-rose-500/15 text-rose-400', 'bg-emerald-500/15 text-emerald-400',
];
const accentFor = (s: string) => ACCENTS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % ACCENTS.length];
const initials = (s: string) => s.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function BrandMark({ name, logoUrl, size = 'md' }: { name: string; logoUrl?: string | null; size?: 'sm' | 'md' }) {
  const [broken, setBroken] = useState(false);
  const box = size === 'sm' ? 'h-8 w-8 text-[10px]' : 'h-10 w-10 text-xs';
  if (logoUrl && !broken) {
    return (
      <div className={`${box} shrink-0 overflow-hidden rounded-lg border border-border bg-background p-1`}>
        <img src={logoUrl} alt="" className="h-full w-full object-contain" onError={() => setBroken(true)} />
      </div>
    );
  }
  return (
    <div className={`${box} flex shrink-0 items-center justify-center rounded-lg font-semibold ${accentFor(name)}`}>
      {initials(name) || '—'}
    </div>
  );
}

// ── Alias editor row ──────────────────────────────────────────────────────────
function AliasRow({ alias, onSaved, onError }: {
  alias: BrandAlias;
  onSaved: (reapplied?: number) => void;
  onError: (msg: string) => void;
}) {
  const [draft, setDraft] = useState<BrandAliasInput>({
    pattern: alias.pattern, match_type: alias.match_type, priority: alias.priority, is_active: alias.is_active,
  });
  const [busy, setBusy] = useState(false);

  const dirty =
    draft.pattern !== alias.pattern || draft.match_type !== alias.match_type ||
    draft.priority !== alias.priority || draft.is_active !== alias.is_active;

  const run = async (fn: () => Promise<{ reapplied?: number }>) => {
    setBusy(true);
    try { onSaved((await fn()).reapplied); }
    catch (e) { onError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-md border border-border/60 px-2 py-1.5 transition-colors ${draft.is_active ? 'bg-muted/30' : 'bg-transparent opacity-60'}`}>
      <input
        value={draft.pattern ?? ''}
        onChange={(e) => setDraft((d) => ({ ...d, pattern: e.target.value }))}
        placeholder="raw value to match"
        className={`${fieldSm} w-48 font-mono text-xs`}
      />
      <select value={draft.match_type} onChange={(e) => setDraft((d) => ({ ...d, match_type: e.target.value as BrandMatchType }))} className={fieldSm}>
        <option value="contains">contains</option>
        <option value="exact">exact</option>
      </select>
      <input
        type="number" value={draft.priority ?? 100}
        onChange={(e) => setDraft((d) => ({ ...d, priority: parseInt(e.target.value, 10) || 0 }))}
        title="Priority (lower = matched first)" className={`${fieldSm} w-16`}
      />
      <button
        type="button" onClick={() => setDraft((d) => ({ ...d, is_active: !d.is_active }))}
        className={`h-8 rounded-md border px-2 text-xs transition-colors ${draft.is_active ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-border text-muted-foreground'}`}
        title={draft.is_active ? 'Active — click to disable' : 'Inactive — click to enable'}
      >
        {draft.is_active ? 'active' : 'inactive'}
      </button>
      <div className="ml-auto flex items-center gap-1">
        {dirty && (
          <button type="button" className={btnPrimarySm} onClick={() => run(() => brandService.updateAlias(alias.id, draft))} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        )}
        <button type="button" className={btnGhostSm} onClick={() => run(() => brandService.removeAlias(alias.id))} disabled={busy} title="Delete alias">
          <Trash2 className="h-4 w-4 text-destructive" />
        </button>
      </div>
    </div>
  );
}

function AddAlias({ brandId, onSaved, onError }: {
  brandId: number;
  onSaved: (reapplied?: number) => void;
  onError: (msg: string) => void;
}) {
  const [pattern, setPattern] = useState('');
  const [matchType, setMatchType] = useState<BrandMatchType>('contains');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!pattern.trim()) return;
    setBusy(true);
    try {
      const res = await brandService.createAlias({ brand_id: brandId, pattern: pattern.trim(), match_type: matchType });
      setPattern('');
      onSaved(res.reapplied);
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border px-2 py-1.5">
      <input
        value={pattern} onChange={(e) => setPattern(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
        placeholder="Add a raw value, e.g. 'rader gmbh'" className={`${fieldSm} w-48 font-mono text-xs`}
      />
      <select value={matchType} onChange={(e) => setMatchType(e.target.value as BrandMatchType)} className={fieldSm}>
        <option value="contains">contains</option>
        <option value="exact">exact</option>
      </select>
      <button type="button" className={btnOutlineSm} onClick={add} disabled={busy || !pattern.trim()}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
      </button>
    </div>
  );
}

// ── Brand row (collapsed summary → expandable editor) ─────────────────────────
function BrandRow({ brand, maxCount, expanded, onToggle, onChanged, onError }: {
  brand: Brand;
  maxCount: number;
  expanded: boolean;
  onToggle: () => void;
  onChanged: (reapplied?: number) => void;
  onError: (msg: string) => void;
}) {
  const [draft, setDraft] = useState<BrandInput>({
    name: brand.name, slug: brand.slug, logo_url: brand.logo_url,
    sort_order: brand.sort_order, is_active: brand.is_active,
  });
  const [busy, setBusy] = useState(false);

  // Re-sync when the row is refreshed from the server.
  useEffect(() => {
    setDraft({ name: brand.name, slug: brand.slug, logo_url: brand.logo_url, sort_order: brand.sort_order, is_active: brand.is_active });
  }, [brand]);

  const dirty =
    draft.name !== brand.name || draft.slug !== brand.slug || draft.logo_url !== brand.logo_url ||
    draft.sort_order !== brand.sort_order || draft.is_active !== brand.is_active;

  const save = async () => {
    if (!draft.name?.trim() || !draft.slug?.trim()) return onError('Name and slug are required');
    setBusy(true);
    try { onChanged((await brandService.update(brand.id, draft)).reapplied); }
    catch (e) { onError((e as Error).message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete brand "${brand.name}" and its ${brand.aliases?.length ?? 0} alias(es)? Products will be re-mapped.`)) return;
    setBusy(true);
    try { onChanged((await brandService.remove(brand.id)).reapplied); }
    catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  const count = brand.product_count ?? 0;
  const aliases = brand.aliases ?? [];
  const pct = maxCount > 0 ? Math.max(2, (count / maxCount) * 100) : 0;

  return (
    <div className={`overflow-hidden rounded-lg border bg-card transition-colors ${expanded ? 'border-primary/40' : 'border-border hover:border-primary/25'} ${brand.is_active ? '' : 'opacity-70'}`}>
      {/* Summary bar — click anywhere to expand */}
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 px-3 py-2.5 text-left">
        <BrandMark name={brand.name} logoUrl={draft.logo_url} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-foreground">{brand.name}</span>
            {!brand.is_active && <Badge variant="outline" className="gap-1 text-[10px]"><EyeOff className="h-3 w-3" />hidden</Badge>}
          </div>
          <span className="font-mono text-xs text-muted-foreground">/{brand.slug}</span>
        </div>

        {/* Alias summary */}
        <div className="hidden max-w-[26%] items-center gap-1 md:flex">
          {aliases.slice(0, 2).map((a) => (
            <span key={a.id} className="truncate rounded bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{a.pattern}</span>
          ))}
          {aliases.length > 2 && <span className="text-[11px] text-muted-foreground">+{aliases.length - 2}</span>}
          {aliases.length === 0 && <span className="text-[11px] text-amber-500">no aliases</span>}
        </div>

        {/* Product volume */}
        <div className="hidden w-32 shrink-0 sm:block">
          <div className="mb-1 text-right text-xs tabular-nums text-foreground">{count.toLocaleString()}</div>
          <div className="h-1 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary/70" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {/* Editor */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="border-t border-border px-3 py-3">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Display name</label>
                  <input value={draft.name ?? ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} className={field} />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Slug</label>
                  <input value={draft.slug ?? ''} onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))} className={`${field} font-mono`} />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Logo URL</label>
                  <input value={draft.logo_url ?? ''} onChange={(e) => setDraft((d) => ({ ...d, logo_url: e.target.value }))} className={field} placeholder="/brands/x.svg" />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Sort order</label>
                  <input type="number" value={draft.sort_order ?? 0} onChange={(e) => setDraft((d) => ({ ...d, sort_order: parseInt(e.target.value, 10) || 0 }))} className={field} />
                </div>
              </div>

              <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-sm text-foreground">
                <input type="checkbox" checked={!!draft.is_active} onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))} />
                Visible in the Trade Portal
              </label>

              <div className="mt-4 space-y-2 border-t border-border pt-3">
                <div className="text-xs font-medium text-muted-foreground">
                  Aliases — raw values that roll up to <span className="text-foreground">{brand.name}</span>
                </div>
                {aliases.map((a) => <AliasRow key={a.id} alias={a} onSaved={onChanged} onError={onError} />)}
                <AddAlias brandId={brand.id} onSaved={onChanged} onError={onError} />
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <button type="button" className={`${BTN} h-8 px-2.5 border-transparent bg-transparent text-destructive hover:bg-destructive/10`} onClick={remove} disabled={busy}>
                  <Trash2 className="h-4 w-4" /> Delete brand
                </button>
                <button type="button" className={btnPrimarySm} onClick={save} disabled={busy || !dirty}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {dirty ? 'Save changes' : 'Saved'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Add brand ─────────────────────────────────────────────────────────────────
function AddBrand({ onAdded, onError }: { onAdded: () => void; onError: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<BrandInput>({ name: '', slug: '', logo_url: '', sort_order: 100, is_active: true });
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!draft.name?.trim() || !draft.slug?.trim()) return onError('Name and slug are required');
    setBusy(true);
    try {
      await brandService.create(draft);
      setDraft({ name: '', slug: '', logo_url: '', sort_order: 100, is_active: true });
      setOpen(false);
      onAdded();
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  if (!open) {
    return <button type="button" className={btnOutline} onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Add brand</button>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end gap-2 rounded-lg border border-primary/30 bg-card p-3">
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Name</label>
        <input autoFocus value={draft.name ?? ''} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value, slug: d.slug || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') }))} className={`${field} w-40`} />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Slug</label>
        <input value={draft.slug ?? ''} onChange={(e) => setDraft((d) => ({ ...d, slug: e.target.value }))} className={`${field} w-40 font-mono`} />
      </div>
      <div>
        <label className="mb-1 block text-xs text-muted-foreground">Logo URL</label>
        <input value={draft.logo_url ?? ''} onChange={(e) => setDraft((d) => ({ ...d, logo_url: e.target.value }))} className={`${field} w-44`} placeholder="/brands/x.svg" />
      </div>
      <button type="button" className={btnPrimary} onClick={create} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create
      </button>
      <button type="button" className={btnGhostSm} onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
    </motion.div>
  );
}

// ── Unmapped values (collapsible) ─────────────────────────────────────────────
function UnmappedRow({ item, brands, onChanged, onError }: {
  item: UnmappedBrand;
  brands: Brand[];
  onChanged: (reapplied?: number) => void;
  onError: (msg: string) => void;
}) {
  const [brandId, setBrandId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);

  const link = async () => {
    if (!brandId) return;
    setBusy(true);
    try { onChanged((await brandService.createAlias({ brand_id: Number(brandId), pattern: item.raw_value, match_type: 'contains' })).reapplied); }
    catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  const createNew = async () => {
    setBusy(true);
    try {
      const slug = item.raw_value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'brand';
      const brand = await brandService.create({ name: item.raw_value, slug, is_active: true, sort_order: 100 });
      onChanged((await brandService.createAlias({ brand_id: brand.id, pattern: item.raw_value, match_type: 'contains' })).reapplied);
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-background px-2.5 py-1.5">
      <span className="font-mono text-sm text-foreground">{item.raw_value}</span>
      <span className="text-xs text-muted-foreground">{item.product_count} product{item.product_count === 1 ? '' : 's'}</span>
      <div className="ml-auto flex items-center gap-1.5">
        <select value={brandId} onChange={(e) => setBrandId(e.target.value ? Number(e.target.value) : '')} className={fieldSm}>
          <option value="">Link to…</option>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <button type="button" className={btnOutlineSm} onClick={link} disabled={busy || !brandId}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Link
        </button>
        <button type="button" className={btnOutlineSm} onClick={createNew} disabled={busy} title="Create a new brand from this value">
          <Sparkles className="h-4 w-4" /> New
        </button>
      </div>
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────
type SortKey = 'order' | 'name' | 'products';

export default function BrandsPanel({ onError, onNotice }: {
  onError: (msg: string) => void;
  onNotice: (msg: string) => void;
}) {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [unmapped, setUnmapped] = useState<UnmappedBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('order');
  const [hideInactive, setHideInactive] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showUnmapped, setShowUnmapped] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bs, um] = await Promise.all([brandService.list(), brandService.listUnmapped()]);
      setBrands(bs);
      setUnmapped(um);
    } catch (e) { onError((e as Error).message); } finally { setLoading(false); }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const handleChanged = useCallback((reapplied?: number) => {
    if (typeof reapplied === 'number') onNotice(`${reapplied} product${reapplied === 1 ? '' : 's'} re-mapped`);
    load();
  }, [load, onNotice]);

  const reapply = async () => {
    setApplying(true);
    try {
      const n = await brandService.apply();
      onNotice(`Re-applied mappings — ${n} product${n === 1 ? '' : 's'} updated`);
    } catch (e) { onError((e as Error).message); } finally { setApplying(false); }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = brands.filter((b) => {
      if (hideInactive && !b.is_active) return false;
      if (!q) return true;
      return b.name.toLowerCase().includes(q) || b.slug.toLowerCase().includes(q)
        || (b.aliases ?? []).some((a) => a.pattern.toLowerCase().includes(q));
    });
    const sorted = [...rows];
    if (sort === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'products') sorted.sort((a, b) => (b.product_count ?? 0) - (a.product_count ?? 0));
    else sorted.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name));
    return sorted;
  }, [brands, search, sort, hideInactive]);

  const maxCount = useMemo(() => Math.max(1, ...brands.map((b) => b.product_count ?? 0)), [brands]);
  const totalProducts = useMemo(() => brands.reduce((n, b) => n + (b.product_count ?? 0), 0), [brands]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brands, slugs or aliases…"
            className={`${field} pl-8 pr-8`}
          />
          {search && (
            <button type="button" onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} className={field + ' w-auto'} title="Sort brands">
          <option value="order">Sort order</option>
          <option value="name">Name A–Z</option>
          <option value="products">Most products</option>
        </select>

        <button
          type="button" onClick={() => setHideInactive((v) => !v)}
          className={`${BTN} h-9 px-3 ${hideInactive ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border bg-transparent hover:bg-muted'}`}
        >
          <EyeOff className="h-4 w-4" /> Active only
        </button>

        <button type="button" className={btnOutline} onClick={reapply} disabled={applying}>
          {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-apply mappings
        </button>
        <AddBrand onAdded={load} onError={onError} />
      </div>

      {/* Stats */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span><span className="font-medium text-foreground">{brands.length}</span> brands</span>
        <span><span className="font-medium text-foreground">{totalProducts.toLocaleString()}</span> products mapped</span>
        {unmapped.length > 0 && <span className="text-amber-500"><span className="font-medium">{unmapped.length}</span> unmapped values</span>}
        <span className="ml-auto">Editing here updates <code className="text-foreground">products.brand_name_cleaned</code> across the catalogue.</span>
      </div>

      {/* Unmapped */}
      {!loading && unmapped.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.04]">
          <button type="button" onClick={() => setShowUnmapped((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-500" />
            <span className="text-sm font-medium text-foreground">Unmapped brand values</span>
            <Badge variant="outline" className="border-amber-500/40 text-amber-500">{unmapped.length}</Badge>
            <span className="ml-2 hidden truncate text-xs text-muted-foreground sm:inline">
              These show under their raw name in the Trade Portal
            </span>
            <ChevronDown className={`ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform ${showUnmapped ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence initial={false}>
            {showUnmapped && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }}>
                <div className="space-y-1.5 px-3 pb-3">
                  {unmapped.map((u) => <UnmappedRow key={u.raw_value} item={u} brands={brands} onChanged={handleChanged} onError={onError} />)}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg border border-border bg-card/60" />)}
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground">
          {brands.length === 0 ? 'No brands yet. Add one to get started.' : `No brands match "${search}"`}
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((b) => (
            <BrandRow
              key={b.id}
              brand={b}
              maxCount={maxCount}
              expanded={expandedId === b.id}
              onToggle={() => setExpandedId((id) => (id === b.id ? null : b.id))}
              onChanged={handleChanged}
              onError={onError}
            />
          ))}
        </div>
      )}
    </div>
  );
}
